import React, { useEffect, useState } from 'react';
import { getStudents } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PaymentModal from '../components/PaymentModal';
import toast from 'react-hot-toast';
import { FiSearch, FiMapPin, FiBook, FiPhone, FiLock, FiFilter } from 'react-icons/fi';

const TutorDashboard = () => {
  const { user, refreshUser } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({ city: '', class: '', subject: '' });
  const [payTarget, setPayTarget] = useState(null);

  const fetchStudents = async () => {
    setLoading(true);
    try { const { data } = await getStudents(filters); setStudents(data.students); }
    catch { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStudents(); }, []);

  const unlockedIds = user?.unlockedContacts?.map(String) || [];

  const StatCard = ({ icon, label, value, color }) => (
    <div style={{ background: 'white', borderRadius: '20px', padding: '20px 24px', boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: '22px', fontFamily: 'var(--font-display)', fontWeight: 800, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '12px', color: 'var(--gray)', marginTop: '3px' }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div style={{ background: 'var(--lighter)', minHeight: '100vh', padding: '32px 0' }}>
      <div className="container-fluid px-4 px-lg-5">
        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 800, marginBottom: '4px' }}>
            Welcome back, {user?.name?.split(' ')[0]}! 👋
          </h1>
          <p style={{ color: 'var(--gray)', fontSize: '15px', marginBottom: 0 }}>Find students looking for tutors in your area</p>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3"><StatCard icon="🔓" label="Contacts Unlocked" value={unlockedIds.length} color="#EEF2FF" /></div>
          <div className="col-6 col-md-3"><StatCard icon="📚" label="Subjects" value={user?.tutorProfile?.subjects?.length || 0} color="#ECFDF5" /></div>
          <div className="col-6 col-md-3"><StatCard icon="💰" label="Fee / hr" value={user?.tutorProfile?.feePerHour ? `₹${user.tutorProfile.feePerHour}` : '—'} color="#FFF7ED" /></div>
          <div className="col-6 col-md-3"><StatCard icon="🎓" label="Experience" value={user?.tutorProfile?.experience ? `${user.tutorProfile.experience} yrs` : '—'} color="#FEF2F2" /></div>
        </div>

        {/* Search */}
        <div style={{ background: 'white', borderRadius: '20px', padding: '20px', boxShadow: 'var(--shadow-sm)', marginBottom: '24px' }}>
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray)', marginBottom: '6px', display: 'block' }}>SEARCH</label>
              <div style={{ position: 'relative' }}>
                <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray)' }} />
                <input placeholder="City, area..." value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1.5px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)' }} />
              </div>
            </div>
            <div className="col-md-3">
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray)', marginBottom: '6px', display: 'block' }}>CLASS</label>
              <select value={filters.class} onChange={e => setFilters(f => ({ ...f, class: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)' }}>
                <option value="">All Classes</option>
                {['Class 8','Class 9','Class 10','Class 11','Class 12'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray)', marginBottom: '6px', display: 'block' }}>SUBJECT</label>
              <select value={filters.subject} onChange={e => setFilters(f => ({ ...f, subject: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-body)' }}>
                <option value="">All Subjects</option>
                {['Maths','Physics','Chemistry','Biology','English','Computer Science'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <button onClick={fetchStudents} className="btn-primary-custom" style={{ width: '100%', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <FiFilter size={14} /> Search
              </button>
            </div>
          </div>
        </div>

        {/* Student cards */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="spinner-border" style={{ color: 'var(--primary)', width: '40px', height: '40px' }} />
          </div>
        ) : (
          <div className="row g-3">
            {students.length === 0 && (
              <div className="col-12" style={{ textAlign: 'center', padding: '60px', color: 'var(--gray)' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                <p>No students found. Try adjusting your filters.</p>
              </div>
            )}
            {students.map(s => {
              const isUnlocked = unlockedIds.includes(String(s._id));
              const initials = s.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              return (
                <div key={s._id} className="col-sm-6 col-xl-4">
                  <div className="card-hover" style={{ background: 'white', borderRadius: '20px', padding: '22px', boxShadow: 'var(--shadow-sm)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '14px' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'linear-gradient(135deg, #6C63FF22, #6C63FF44)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, color: 'var(--primary)', flexShrink: 0, fontFamily: 'var(--font-display)' }}>{initials}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '16px', marginBottom: '2px' }}>{s.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--gray)' }}>
                          <FiMapPin size={11} />{s.city}
                        </div>
                      </div>
                      <span style={{ padding: '4px 10px', background: '#EEF2FF', color: 'var(--primary)', borderRadius: '8px', fontSize: '11px', fontWeight: 700 }}>{s.studentProfile?.class}</span>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                      {s.studentProfile?.subjects?.map(sub => (
                        <span key={sub} style={{ padding: '3px 10px', background: 'var(--light)', borderRadius: '6px', fontSize: '11px', fontWeight: 600, color: 'var(--dark3)' }}>{sub}</span>
                      ))}
                    </div>

                    {s.studentProfile?.requirement && (
                      <p style={{ fontSize: '13px', color: 'var(--gray)', lineHeight: 1.5, marginBottom: '14px', flex: 1 }}>"{s.studentProfile.requirement}"</p>
                    )}

                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {isUnlocked ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiPhone size={14} color="var(--success)" /></div>
                          <div>
                            <div style={{ fontSize: '11px', color: 'var(--gray)' }}>Contact</div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--success)' }}>{s.phone}</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--gray)' }}>
                          <FiLock size={13} /> Contact hidden
                        </div>
                      )}
                      {!isUnlocked && (
                        <button onClick={() => setPayTarget(s)} className="btn-primary-custom" style={{ padding: '8px 16px', fontSize: '12px' }}>
                          Unlock ₹50
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {payTarget && (
        <PaymentModal
          targetUser={payTarget}
          onClose={() => setPayTarget(null)}
          onSuccess={() => { setPayTarget(null); fetchStudents(); toast.success('Contact unlocked!'); }}
        />
      )}
    </div>
  );
};

export default TutorDashboard;
